import { useEffect, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { PhotoStage, PhotoControls, usePhotoGestures } from "@/components/PhotoStage";
import { PhotoFixAction } from "@/components/PhotoFixAction";
import { MainPhotoAction } from "@/components/MainPhotoAction";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// THE ONE WAY A PHOTO OPENS, ANYWHERE IN THE APP.
//
// HIS INSTRUCTION, 2026-10-15: *"Every place a photo can be opened should use that SAME viewer —
// one implementation, not several."* Zoom, rotate, drag to pan, next/previous, and it works with a
// glove on a tablet. The looking itself lives in `PhotoStage` (one gesture implementation); this
// adds the full-screen frame: which photo of how many, paging, close, and the actions that belong
// on a photo somebody is finally looking at properly.
//
// OPENING A PHOTO FULL-SCREEN IS THE MOMENT SOMEBODY NOTICES IT'S WRONG (his point), so the fix
// lives here too — pass fixScope + fixRecordId and the same shared action appears on the photo.
//
// NOTHING IS OFFERED OVER A PHOTO NOBODY CAN SEE YET. The actions used to render the instant the
// viewer opened, so on a slow photo the crew got "Wrong photo?" on a black screen. `AuthImg`
// reports its state and both actions wait for "loaded"; a photo that FAILS shows its honest
// broken-photo placeholder and still offers the fix, because an unloadable photo is exactly the
// kind you want to report.
export function PhotoLightbox({ photos, index, onClose, onIndex, testidPrefix = "lightbox",
                                fixScope, fixRecordId, onFixed,
                                mainScope = "donor", mainRecordId, mainPhotoFileId, resolvedMainFileId, onMainChanged }) {
  const total = photos?.length || 0;
  const p = index == null ? null : photos?.[index];
  // THE STATE BELONGS TO ONE PHOTO (2 Nov): a photo known to be missing says so at once, before a reset could put
  // "Loading the photo…" back over it for ever — a new photo is unseen simply because its id is new.
  const [shotOf, setShotOf] = useState({ id: null, s: "loading" });
  const shot = shotOf.id === p?.file_id ? shotOf.s : "loading";
  const setShot = useCallback((s) => setShotOf({ id: p?.file_id, s }), [p?.file_id]);
  const g = usePhotoGestures(p?.file_id);
  const go = useCallback((delta) => {
    if (!total) return;
    onIndex((index + delta + total) % total);
  }, [index, total, onIndex]);

  // THE KEYBOARD A DESK WORKER ALREADY EXPECTS, and the same keys the old viewer had.
  useEffect(() => {
    if (index == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "+" || e.key === "=") g.zoomIn();
      else if (e.key === "-" || e.key === "_") g.zoomOut();
      else if (e.key === "r" || e.key === "R") g.rotate();
      else if (e.key === "0") g.reset();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, go, onClose, g]);

  if (index == null || !total || !p) return null;
  const onScreen = shot === "loaded" || shot === "failed";
  // STRAIGHT ONTO THE PAGE, NOT INSIDE WHOEVER OPENED IT. Found while testing the part page,
  // 2026-10-15: the viewer opened *inside* the photo workspace, and the workspace sits in a
  // stacking context the sticky header beat — so the header painted over the top of the photo and
  // the Close button could not be tapped. A portal puts the viewer above everything, from every
  // screen, whatever that screen's layout is doing.
  return createPortal((
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
      data-testid={`${testidPrefix}-overlay`}
      role="dialog" aria-modal="true" aria-label="Photo viewer"
      onClick={onClose}
    >
      <div className="absolute top-4 left-4 text-white/70 text-sm font-mono z-10" data-testid={`${testidPrefix}-counter`}>{index + 1} / {total}</div>
      <button className="absolute top-3 right-4 text-white/80 hover:text-white p-1 z-10" onClick={onClose} data-testid={`${testidPrefix}-close`} title="Close (Esc)">
        <X className="w-7 h-7" />
      </button>
      {total > 1 && (
        <button className="absolute left-2 md:left-6 z-10 text-white/60 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid={`${testidPrefix}-prev`} title="Previous (←)">
          <ChevronLeft className="w-9 h-9" />
        </button>
      )}
      <div className="w-[92vw] h-[86dvh] flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <PhotoStage fileId={p.file_id} alt={`photo ${index + 1}`} gestures={g} onState={setShot}
          missing={!!p.bytes_missing}
          reserve="min(60vh, 60vw)" controls={false} testidPrefix={testidPrefix}
          className="flex-1 min-h-0" />
        {shot === "loading" && (
          <span data-testid={`${testidPrefix}-loading`} className="text-white/60 text-xs">
            Loading the photo…
          </span>
        )}
        <div className="flex flex-wrap items-center justify-center gap-2 shrink-0">
          <PhotoControls g={g} testidPrefix={testidPrefix} />
          {shot === "loaded" && mainRecordId && (
            <MainPhotoAction scope={mainScope} recordId={mainRecordId} fileId={p.file_id}
              isMain={resolvedMainFileId === p.file_id}
              isOverride={!!mainPhotoFileId && mainPhotoFileId === p.file_id}
              onChanged={onMainChanged} testid={`${testidPrefix}-main`} />
          )}
          {onScreen && fixScope && fixRecordId && (
            <PhotoFixAction scope={fixScope} recordId={fixRecordId} photo={p} testid={`${testidPrefix}-fix`}
              onDone={() => { onClose(); onFixed?.(); }} />
          )}
        </div>
      </div>
      {total > 1 && (
        <button className="absolute right-2 md:right-6 z-10 text-white/60 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); go(1); }} data-testid={`${testidPrefix}-next`} title="Next (→)">
          <ChevronRight className="w-9 h-9" />
        </button>
      )}
    </div>
  ), document.body);
}
